import { ErrorHandler, Inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { APP_CONFIG, appConfig } from './models/constants';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { EyAuraPreset } from './theme/ey-aura-preset';
import { CookieService } from 'ngx-cookie-service';
import { TokenInterceptor } from './services/interceptors/token.interceptor';
import { QueryParameterInterceptor } from './services/interceptors/query-parameter.interceptor';
import { HttpRequestCancelInterceptor } from './services/interceptors/httprequestcancel.interceptor';
import { AutoLogoutService } from './services/auto-logout.service';
import { HttpCancelRequestService } from './services/http-cancel-request.service';
import { EncryptDecryptService } from './services/encrypt-decrypt.service';
import { AuthGuard } from './services/auth-guard/auth-guard-service';
import { MSALAuthGuard } from './services/auth-guard/msalauth-Guard-service';
import { BnNgIdleService } from 'bn-ng-idle';
import { PreloginComponent } from './prelogin/prelogin.component';
import { UnauthorizedPageComponent } from './unauthorized-page.component';
import { PageNotFoundComponent } from './page-not-found.component';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { DatePipe } from '@angular/common';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslateService, TranslatePipe } from '@ngx-translate/core';
import { PrenavbarComponent } from './prelogin/prenavbar/prenavbar.component';
import { NgHttpLoaderComponent, pendingRequestsInterceptor$ } from 'ng-http-loader';
import { ApplicationInsightsErrorHandler } from './services/app-insights/appInsights-errorhandler.service';
import { DialogService } from 'primeng/dynamicdialog';
import { KsaInterceptor } from './services/interceptors/ksa.interceptor';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation } from '@azure/msal-browser';
import { MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';
import { PreAuthGuard } from './services/auth-guard/preauth.guard';
import { DateTimeService } from './services/date-time/date-time.service';
import { LayoutComponent } from './layout.component';

const nonce =
  document
    .querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')
    ?.content ?? '';
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: appConfig?.appConfigService.azureAdConfig?.clientId,
      authority: appConfig?.appConfigService.azureAdConfig?.authority,
      redirectUri: appConfig?.appConfigService.azureAdConfig?.redirectUri,
      postLogoutRedirectUri: appConfig?.appConfigService.azureAdConfig?.postLogoutRedirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(appConfig.appConfigService.baseApiUrl, ['user.read', 'profile']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      redirectUri: appConfig?.appConfigService.azureAdConfig?.redirectUri,
      authority: appConfig?.appConfigService.azureAdConfig?.authority,
      scopes: ['user.read']
    },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    LayoutComponent,
    PreloginComponent,
    PrenavbarComponent,
    UnauthorizedPageComponent,
    PageNotFoundComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    MsalModule,
    BrowserAnimationsModule,
    DialogModule,
    ConfirmDialogModule,
    ButtonModule,
    NgHttpLoaderComponent,
    TranslatePipe
  ],
  providers: [
    provideHttpClient(withInterceptors([pendingRequestsInterceptor$]), withInterceptorsFromDi(), withXhr()),
    provideTranslateService({
      fallbackLang: 'IN',
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' })
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: QueryParameterInterceptor,
      multi: true,
    },
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestCancelInterceptor,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KsaInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: ErrorHandler,
      useClass: ApplicationInsightsErrorHandler,
    },
    providePrimeNG({
      theme: {
        preset: EyAuraPreset,
        options: {
          darkModeSelector: false
        }
      },
      csp: {
        nonce: nonce
      }
    }),
    MsalGuard,
    MsalBroadcastService,
    MsalService,
    AutoLogoutService,
    ConfirmationService,
    HttpCancelRequestService,
    AuthGuard,
    PreAuthGuard,
    CookieService,
    EncryptDecryptService,
    MSALAuthGuard,
    BnNgIdleService,
    DatePipe,
    DialogService,
    DateTimeService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  exports: []
})
export class AppModule {
  constructor(@Inject(APP_CONFIG) public configData) {
    appConfig.appConfigService = configData;
    appConfig.updateConstant(configData);
  }
 }
